import { StorageError } from '../../app/errors';
import type { AnnotationRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * `annotations` — free-text notes on one hypothesis of one match (B spec §4.1, scenario 17).
 *
 * **Mutable rows — the `matchNotes` INV-14 precedent** — and never an `Observation`: nothing
 * here can reach a count, because `replay`/`reconcile` take `Observation[]` only. Match-scoped:
 * the row appears in the INV-8 cascade and in archives, and nowhere else.
 *
 * The B-2 `WriteAnnotation`/`DeleteAnnotation` command writers land beside these in their own
 * unit; B-1 ships what the import, the cascade, and the reads need.
 */

/**
 * Writes one new annotation. `add`, not `put` — BAK-2 writes into a profile the same
 * transaction has just emptied, so a colliding id means the wipe did not happen.
 *
 * Two callers, one insert (B-2): the import path, and `WriteAnnotation`'s insert arm — whose
 * handler has already read the id as absent inside this same transaction, so the `add` shape
 * costs it nothing and a second insert function would be the sibling copy the reuse contract's
 * duplication gate exists to catch.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id, or on a write failure.
 */
export async function insertAnnotation(row: AnnotationRow): Promise<void> {
  requireTransaction('insertAnnotation');
  try {
    await db().annotations.add(row);
  } catch (cause) {
    throw new StorageError('Could not save the annotation.', {
      cause,
      context: { store: TABLE.annotations, id: row.id, matchId: row.matchId },
    });
  }
}

/** B-2 — the one-row read the bus's snapshots use. @throws {StorageError} on a read failure. */
export async function getAnnotation(id: string): Promise<AnnotationRow | undefined> {
  try {
    return await db().annotations.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the annotation.', {
      cause,
      context: { store: TABLE.annotations, id },
    });
  }
}

/**
 * A match's annotations, oldest first (id tie-break) — the order the detail sheet and the
 * match record list them. Narrow to one hypothesis via the `[matchId+hypothesisId]` index by
 * passing `hypothesisId`.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listForMatch(
  matchId: string,
  hypothesisId?: string,
): Promise<AnnotationRow[]> {
  try {
    const rows =
      hypothesisId === undefined
        ? await db().annotations.where('matchId').equals(matchId).toArray()
        : await db()
            .annotations.where('[matchId+hypothesisId]')
            .equals([matchId, hypothesisId])
            .toArray();
    return rows.sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
  } catch (cause) {
    throw new StorageError('Could not read the match’s annotations.', {
      cause,
      context: { store: TABLE.annotations, matchId, hypothesisId },
    });
  }
}

/**
 * B-2 — the `updateAnnotation` op's writer: `text` alone.
 *
 * The storage-layer mirror of the op's key union (`matchNotes.updateNote`'s shape): `matchId`,
 * `hypothesisId` and `createdAt` are unreachable from here, so an edit cannot re-home or
 * re-date a note, and `updatedAt` comes from the bus's clock rather than the caller's row.
 *
 * @throws {StorageError} outside a transaction, if the annotation is missing, or on a write failure.
 */
export async function updateAnnotation(
  id: string,
  changes: Pick<AnnotationRow, 'text'>,
  updatedAt: number,
): Promise<void> {
  requireTransaction('updateAnnotation');
  const row = await db().annotations.get(id);
  if (!row) {
    throw new StorageError(`Annotation ${id} no longer exists; nothing was written.`, {
      context: { store: TABLE.annotations, id },
    });
  }
  try {
    await db().annotations.put({ ...row, text: changes.text, updatedAt });
  } catch (cause) {
    throw new StorageError('Could not update the annotation.', {
      cause,
      context: { store: TABLE.annotations, id },
    });
  }
}

/**
 * B-2 — the `deleteAnnotation` op's writer. Presence (and this-match ownership) is the
 * handler's question, answered as `ERR_ANNOTATION_NOT_FOUND` from the in-transaction read;
 * this is the mechanical half.
 *
 * @throws {StorageError} outside a transaction or on a write failure.
 */
export async function deleteAnnotation(id: string): Promise<void> {
  requireTransaction('deleteAnnotation');
  try {
    await db().annotations.delete(id);
  } catch (cause) {
    throw new StorageError('Could not delete the annotation.', {
      cause,
      context: { store: TABLE.annotations, id },
    });
  }
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteAnnotationsForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteAnnotationsForCascade');
  if (ids.length === 0) return;
  try {
    await db().annotations.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the annotations during a cascade.', {
      cause,
      context: { store: TABLE.annotations, count: ids.length },
    });
  }
}
